import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';
import SEO from '../components/common/SEO';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const fallbackImages = {
  banner: '/assets/img/breadcrumb/vendor-banner.jpg',
  logo: '/mangoloops-fevicon.png',
  holiday: '/assets/img/hotels/hotels-places/del-sm.webp',
  hotel: '/assets/img/hotels/hotels-places/del-sm.webp',
  cab: '/assets/img/hotels/hotels-places/del-sm.webp',
  medical: '/assets/img/medical-tourism/banner.jpg',
};

const parseCategories = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return String(value).split(',').map(item => item.trim()).filter(Boolean);
  }
};

const normalizeImage = (image, fallback) => {
  if (!image) return fallback;
  if (/^https?:\/\//i.test(image)) return image;
  return image.startsWith('/') ? image : `/${image}`;
};

const slugify = (value) => value
  ? String(value).toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/--+/g, '-').replace(/^-+|-+$/g, '')
  : '';

const toTitleCase = (value) => String(value)
  .toLocaleLowerCase()
  .replace(/\b\w/g, character => character.toLocaleUpperCase());

const createDetailSlug = (...parts) => parts.filter(part => part !== null && part !== undefined && part !== '')
  .map(part => typeof part === 'number' ? part : slugify(part))
  .filter(Boolean)
  .join('-');

const parseSocialLinks = (value) => {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  try { return JSON.parse(value) || {}; } catch { return {}; }
};

const socialPlatforms = [
  ['instagram', 'Instagram', 'bi-instagram'],
  ['facebook', 'Facebook', 'bi-facebook'],
  ['linkedin', 'LinkedIn', 'bi-linkedin'],
  ['twitter', 'X / Twitter', 'bi-twitter-x'],
  ['youtube', 'YouTube', 'bi-youtube'],
  ['pinterest', 'Pinterest', 'bi-pinterest'],
];

const getLocation = (vendor) => {
  return [vendor?.operational_city || vendor?.company_city, vendor?.operational_state || vendor?.company_state, 'India']
    .filter(Boolean)
    .join(', ');
};

const Section = ({ title, children, show = true }) => {
  if (!show) return null;
  return (
    <section className="vendor-profile-block">
      <h2>{title}</h2>
      {children}
    </section>
  );
};

const ScrollCards = ({ items, type, fallbackImage }) => (
  <div className="vendor-scroll-wrapper">
    <div className="vendor-scrolling-row">
      {items.map(item => (
        <Link to={item.link || '#'} className={`vendor-service-card ${type}`} key={`${type}-${item.id || item.title}`}>
          <div className="vendor-card-img">
            <img src={normalizeImage(item.image, fallbackImage)} alt={item.title} />
          </div>
          <div className="vendor-card-title">{item.title}</div>
          {item.subtitle && <div className="vendor-card-subtitle">{item.subtitle}</div>}
        </Link>
      ))}
    </div>
  </div>
);

function VendorDetails() {
  const { company_slug, encoded_id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(Boolean(encoded_id));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!encoded_id) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError('');

    const profilePath = company_slug
      ? `${encodeURIComponent(company_slug)}/${encodeURIComponent(encoded_id)}`
      : encodeURIComponent(encoded_id);

    axios.get(`${API_BASE_URL}/vendors/public-profile/${profilePath}`)
      .then((response) => {
        if (isMounted) setProfile(response.data);
      })
      .catch(() => {
        if (isMounted) setError('Vendor profile could not be loaded.');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [company_slug, encoded_id]);

  const vendor = profile?.vendor || {};
  const services = profile?.services || {};
  const categories = useMemo(() => parseCategories(vendor.business_categories), [vendor.business_categories]);
  const socialLinks = useMemo(() => parseSocialLinks(vendor.social_media_links), [vendor.social_media_links]);

  console.log('render categories:', categories);
  const holidayCards = (services.holidayPackages || []).map(item => ({
    id: item.id,
    title: item.package_name || item.city || 'Holiday Package',
    subtitle: item.city,
    image: item.image,
    link: `/holiday-details/${createDetailSlug(
      item.package_name,
      item.city,
      Math.round(item.offer_price || item.package_price || 0),
      item.id
    )}`,
  }));

  const hotelCards = (services.hotels || []).map(item => ({
    id: item.id,
    title: item.hotel_name || item.city || 'Hotel',
    subtitle: item.city,
    image: item.image,
    link: `/hotel-details/${createDetailSlug(
      item.hotel_name,
      item.city,
      Math.round(item.offer_price || item.hotel_price || 0),
      item.id
    )}`,
  }));

  const cabCards = (services.cabs || []).map(item => ({
    id: item.id,
    title: item.brand_name || 'Cab Service',
    subtitle: [item.cab_city, item.accommodation_type, item.seating_capacity && `${item.seating_capacity} Seats`].filter(Boolean).join(' | '),
    image: item.image,
  }));

  const yatraCards = (services.yatras || []).map(item => ({
    id: item.id,
    title: item.yatra_name || 'Yatra Package',
    subtitle: item.city,
    image: item.image,
    link: `/yatra-details/${createDetailSlug(
      item.yatra_name,
      item.city,
      Math.round(item.offer_price || item.yatra_price || 0),
      item.id
    )}`,
  }));

  const hospitalCards = (services.hospitals || []).map(item => ({
    id: item.id,
    title: item.hospital_name || 'Hospital',
    subtitle: [item.city, item.state].filter(Boolean).join(', '),
    image: item.image,
    link: `/medical-details/hospital/${createDetailSlug(item.hospital_name, item.city, item.id)}`,
  }));

  const doctorCards = (services.doctors || []).map(item => ({
    id: item.id,
    title: item.doctor_name || 'Doctor',
    subtitle: [item.city, item.total_experience && `${item.total_experience} Exp.`].filter(Boolean).join(' | '),
    image: item.image,
    link: `/medical-details/doctor/${createDetailSlug(item.doctor_name, item.city, item.id)}`,
  }));
  const medicalCards = [...hospitalCards, ...doctorCards];

  const companyName = toTitleCase(vendor.company_name || vendor.name || 'Vendor Profile');
  const address = vendor.operational_address || vendor.company_address || 'Address details will be updated soon.';
  const bannerImage = normalizeImage(vendor.company_banner_path, fallbackImages.banner);
  const logoImage = normalizeImage(vendor.company_logo_path, fallbackImages.logo);
  const aboutText = vendor.about_company || vendor.company_description || vendor.description || vendor.company_info || 'No company information has been added yet.';

  return (
    <div>
      <SEO title={`${companyName} | Vendor Details | Mangoloops`} description={`View ${companyName} services, address, and contact details on Mangoloops.`} />
      <Header />

      <style>{`
        .vendor-profile-page {
          --vendor-primary: #008ccf;
          --vendor-text: #243142;
          --vendor-muted: #64748b;
         
          --vendor-card: #ffffff;
          --vendor-border: #e2e8f0;
          --vendor-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
          background: var(--vendor-bg);
          color: var(--vendor-text);
          font-family: 'Poppins', sans-serif;
        }
        .vendor-hero {
          align-items: flex-end;
          background: #dbeafe;
          display: flex;
          height: 335px;
          justify-content: center;
          overflow: hidden;
          position: relative;
        }
        .vendor-hero img {
          height: 100%;
          object-fit: cover;
          width: 100%;
        }
        .vendor-profile-section {
          margin: -60px auto 30px;
          max-width: 1320px;
          padding: 0 20px;
          position: relative;
        }
        .vendor-profile-card {
          background: var(--vendor-card);
          border: 1px solid var(--vendor-border);
          border-radius: 12px;
          box-shadow: var(--vendor-shadow);
          padding: 30px;
          text-align: center;
        }
        .vendor-logo-wrapper {
          align-items: center;
          background: #fff;
          border-radius: 50%;
          box-shadow: 0 4px 10px rgba(15, 23, 42, 0.16);
          display: flex;
          height: 110px;
          justify-content: center;
          margin: -85px auto 15px;
          overflow: hidden;
          padding: 6px;
          width: 110px;
        }
        .vendor-logo-wrapper img {
          height: 100%;
          object-fit: contain;
          width: 100%;
        }
        .vendor-profile-card h1 {
          color: #162033;
          font-size: 22px;
          font-weight: 700;
          margin-bottom: 6px;
        }
        .vendor-profile-card p {
          color: var(--vendor-muted);
          font-size: 13px;
          margin-bottom: 4px;
        }
        .vendor-category-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: center;
          margin-top: 14px;
        }
        .vendor-category-pills span {
          background: #eef6ff;
          border-radius: 999px;
          color: var(--vendor-primary);
          font-size: 12px;
          font-weight: 700;
          padding: 5px 12px;
        }
        .vendor-contact-icons {
          display: flex;
          flex-wrap: nowrap;
          gap: 14px;
          justify-content: center;
          margin-top: 16px;
          overflow-x: auto;
          scrollbar-width: none;
        }
        .vendor-contact-icons::-webkit-scrollbar {
          display: none;
        }
        .vendor-contact-icons a {
          align-items: center;
          border: 1px solid var(--vendor-border);
          border-radius: 8px;
          color: var(--vendor-primary);
          display: inline-flex;
          flex: 0 0 38px;
          font-size: 18px;
          font-weight: 700;
          height: 38px;
          justify-content: center;
          text-decoration: none;
          width: 38px;
        }
        .vendor-profile-main {
          margin: 0 auto;
          max-width: 1320px;
          padding: 0 20px 60px;
        }
        .vendor-profile-block {
          background: var(--vendor-card);
          border: 1px solid var(--vendor-border);
          border-radius: 12px;
          box-shadow: var(--vendor-shadow);
          margin-bottom: 25px;
          padding: 25px;
        }
        .vendor-profile-block h2 {
          border-left: 4px solid var(--vendor-primary);
          color: #111827;
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 15px;
          padding-left: 10px;
          text-align: left;
        }
        .vendor-about-company {
          overflow-wrap: anywhere;
          white-space: pre-wrap;
        }
        .vendor-profile-block p {
          color: #475569;
          font-size: 13.5px;
          line-height: 1.7;
          margin-bottom: 12px;
          text-align: justify;
        }
        .vendor-scroll-wrapper {
          margin-top: 15px;
          position: relative;
        }
        .vendor-scrolling-row {
          display: flex;
          gap: 15px;
          overflow-x: auto;
          padding-bottom: 10px;
          scroll-behavior: smooth;
          scrollbar-width: none;
        }
        .vendor-scrolling-row::-webkit-scrollbar {
          display: none;
        }
        .vendor-service-card {
          color: inherit;
          flex: 0 0 150px;
          min-width: 150px;
          text-align: center;
          text-decoration: none;
          transition: transform 0.2s ease;
        }
        .vendor-service-card:hover {
          transform: translateY(-4px);
        }
        .vendor-service-card.hotel {
          flex-basis: 155px;
        }
        .vendor-card-img {
          aspect-ratio: 1 / 1;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
          margin-bottom: 8px;
          overflow: hidden;
        }
        .vendor-service-card.hotel .vendor-card-img {
          aspect-ratio: 2 / 3;
        }
        .vendor-card-img img {
          height: 100%;
          object-fit: cover;
          width: 100%;
        }
        .vendor-card-title {
          color: #334155;
          font-size: 12px;
          font-weight: 700;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .vendor-card-subtitle {
          color: var(--vendor-muted);
          font-size: 11px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .vendor-cabs-grid {
          display: grid;
          gap: 15px;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        }
        .vendor-cab-card {
          align-items: center;
          background: #fff;
          border: 1px solid var(--vendor-border);
          border-radius: 12px;
          display: flex;
          gap: 15px;
          padding: 15px;
        }
        .vendor-cab-card img {
          border-radius: 8px;
          height: 54px;
          object-fit: cover;
          width: 54px;
        }
        .vendor-cab-card h3 {
          color: #1f2937;
          font-size: 13px;
          font-weight: 700;
          margin-bottom: 4px;
        }
        .vendor-cab-card p {
          color: var(--vendor-muted);
          font-size: 11px;
          line-height: 1.5;
          margin: 0;
          text-align: left;
        }
        .vendor-state {
          padding: 70px 20px;
          text-align: center;
        }
        @media (max-width: 768px) {
          .vendor-hero { height: 150px; }
          .vendor-profile-section { margin-top: -40px; }
          .vendor-profile-card,
          .vendor-profile-block { padding: 16px; }
          .vendor-logo-wrapper {
            height: 90px;
            margin-top: -65px;
            width: 90px;
          }
        }
      `}</style>

      <div className="vendor-profile-page">
        {loading ? (
          <div className="vendor-state">Loading vendor profile...</div>
        ) : error ? (
          <div className="vendor-state">{error}</div>
        ) : (
          <>
            <div className="vendor-hero">
              <img src={bannerImage} alt={`${companyName} banner`} onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = fallbackImages.banner;
              }} />
            </div>

            <div className="vendor-profile-section">
              <div className="vendor-profile-card">
                <div className="vendor-logo-wrapper">
                  <img src={logoImage} alt={`${companyName} logo`} />
                </div>
                <h1>{companyName}</h1>
                <p>{getLocation(vendor)}</p>
                <p>{address}</p>

                {categories.length > 0 && (
                 
                  <div className="vendor-category-pills">
                    {categories.map(category => <span key={category}>{category}</span>)}
                  </div>
                )}

                <div className="vendor-contact-icons" aria-label="Contact and social links">
                  {vendor.whatsapp_no && <a href={`https://wa.me/${vendor.whatsapp_no}`} aria-label="WhatsApp"><i className="bi bi-whatsapp"></i></a>}
                  {vendor.phone && <a href={`tel:${vendor.phone}`} aria-label="Phone"><i className="bi bi-telephone-fill"></i></a>}
                  {vendor.email && <a href={`mailto:${vendor.email}`} aria-label="Email"><i className="bi bi-envelope-fill"></i></a>}
                  {socialPlatforms.map(([key, label, icon]) => socialLinks[key] ? (
                    <a
                      key={key}
                      href={/^https?:\/\//i.test(socialLinks[key]) ? socialLinks[key] : `https://${socialLinks[key]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      title={label}
                    >
                      <i className={`bi ${icon}`} aria-hidden="true"></i>
                    </a>
                  ) : null)}
                </div>
              </div>
            </div>

            <main className="vendor-profile-main">
             

              <Section title="Holidays Package's" show={holidayCards.length > 0}>
                <ScrollCards items={holidayCards} type="tour" fallbackImage={fallbackImages.holiday} />
              </Section>

              <Section title="Cabs Services" show={cabCards.length > 0}>
                <div className="vendor-cabs-grid">
                  {cabCards.map(item => (
                    <div className="vendor-cab-card" key={`cab-${item.id}`}>
                      <img src={normalizeImage(item.image, fallbackImages.cab)} alt={item.title} />
                      <div>
                        <h3>{item.title}</h3>
                        <p>{item.subtitle || 'Comfortable cab service with vendor support.'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Hotel Services" show={hotelCards.length > 0}>
                <ScrollCards items={hotelCards} type="hotel" fallbackImage={fallbackImages.hotel} />
              </Section>

              <Section title="Yatra Package's" show={yatraCards.length > 0}>
                <ScrollCards items={yatraCards} type="yatra" fallbackImage="/no_image_teerth-yatra.jpg" />
              </Section>

              <Section title="Doctors / Hostitals" show={medicalCards.length > 0}>
                <ScrollCards items={medicalCards} type="medical" fallbackImage={fallbackImages.medical} />
              </Section>

               <Section title="About Company">
                <p className="vendor-about-company">{aboutText}</p>
              </Section>
            </main>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default VendorDetails;
